import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MapPin, Trash2 } from "lucide-react";
import { invalidateGmapsKeyCache } from "@/components/bi/GoogleMapWidget";

// Item 88.1: tenant-level Google Maps JavaScript API key management.
export function GoogleMapsKeyCard({ tenantId }) {
  const [state, setState] = useState({ configured: false, masked: "" });
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/google-maps-key`).then(({ data }) => setState(data)).catch(() => {});
  }, [tenantId]);
  const save = async (val) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/tenants/${tenantId}/google-maps-key`, { key: val });
      setState({ configured: data.configured, masked: data.configured ? val.slice(0, 8) + "…" : "" });
      setKey("");
      invalidateGmapsKeyCache();
      toast.success(data.configured ? "Google Maps key saved" : "Google Maps key removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save key");
    } finally {
      setBusy(false);
    }
  };
  if (!tenantId) return null;
  return (
    <div data-testid="gmaps-key-card" className="rounded-2xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-4 mt-4">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="w-4 h-4 text-[color:var(--brand-ocean)]" />
        <h3 className="text-sm font-semibold text-[color:var(--ink)]">Google Maps</h3>
        {state.configured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Configured · {state.masked}</span>}
      </div>
      <p className="text-[11px] text-[color:var(--ink-3)] mb-2">
        Browser key for the Google Map widget (Maps JavaScript API must be enabled). Get one at console.cloud.google.com → APIs &amp; Services → Credentials.
        Without a key, Google Map widgets show a placeholder — the free Point Map keeps working regardless.
      </p>
      <Label className="text-xs text-[color:var(--ink-3)]">API key</Label>
      <div className="flex gap-1.5 mt-1">
        <Input data-testid="gmaps-key-input" value={key} onChange={(e) => setKey(e.target.value)}
          placeholder={state.configured ? "Replace existing key…" : "AIza…"} className="bg-[color:var(--surface-2)] h-8 text-xs flex-1" />
        <Button data-testid="gmaps-key-save" size="sm" disabled={busy || !key.trim()} onClick={() => save(key.trim())} className="h-8">Save</Button>
        {state.configured && (
          <Button data-testid="gmaps-key-remove" size="sm" variant="outline" disabled={busy} onClick={() => save("")} className="h-8 text-[color:var(--danger)]">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
